import { CircularProgress, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import financeiroApi from 'app/services/api/financeiroApi';
import 'isomorphic-fetch';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { TextLabel } from '../Inputs';

const fetchItens = async (parameter, api, getData, active, setOptions, filterName, pesquisa) => {
  if (active) {
    const response = await financeiroApi.get(api, {
      params: {
        ...parameter,
        [filterName]: pesquisa,
      },
    });

    if (getData) {
      const data = getData(response);
      setOptions(data);
    } else {
      const contentDefault = await response.data.content;
      setOptions(contentDefault);
    }
  }
};

async function fetchItem(id, api, value, setSelected, handleChange) {
  const response = await financeiroApi.get(`${api}/${value}`);
  if (response.status === 200) {
    setSelected(response.data);
    handleChange({ target: { id, value: response.data.id } });
  }
}

const SelectAutoComplete = ({
  api,
  id,
  name,
  handleChange,
  getData,
  parameter,
  open,
  setOpen,
  loading,
  selected,
  renderInput,
  options,
  setOptions,
  filterName,
  getRenderOption,
  disabled,
}) => {
  return (
    <Autocomplete
      id={id}
      open={open}
      onOpen={() => {
        setOpen(true);
        fetchItens(parameter, api, getData, true, setOptions, filterName, '');
      }}
      onClose={() => {
        setOpen(false);
      }}
      getOptionLabel={(option) => option[name]}
      noOptionsText="Nenhum registro encontrado"
      loadingText="Carregando..."
      options={options}
      loading={loading}
      value={selected}
      renderOption={getRenderOption}
      onInputChange={(event, valorPesquisado) => {
        if (open && valorPesquisado === '') {
          fetchItens(parameter, api, getData, open, setOptions, filterName);
        } else if (
          open &&
          options &&
          (options.length === 0 ||
            !options.find((obj) => obj[name].toLowerCase().includes(valorPesquisado.toLowerCase())))
        ) {
          fetchItens(parameter, api, getData, open, setOptions, filterName, valorPesquisado);
        }
      }}
      onChange={(event, val) => {
        const valor = val ? val.id : val;
        handleChange({ target: { id, value: valor } });
      }}
      renderInput={renderInput}
      disabled={disabled}
    />
  );
};

function SelectAsyncPure(props) {
  const {
    api,
    id,
    label,
    name,
    handleBlur,
    handleChange,
    getData,
    error,
    touched,
    required,
    value,
    parameter,
    readOnly,
    filterNameDefault = 'q',
    fetchByValue,
    getRenderOption,
    idObject,
    disabled,
    helperText,
  } = props;
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);

  const loading = open && options && options.length === 0;

  useEffect(() => {
    if (value && typeof value !== 'object') {
      if (fetchByValue) {
        fetchByValue(id, idObject, api, value, setSelected, handleChange);
      } else {
        fetchItem(id, api, value, setSelected, handleChange);
      }
    } else {
      setSelected(null);
    }
    // A função é recriada a cada render; incluí-la reexecutaria a busca em loop.
    // O efeito depende apenas do gatilho listado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    let active = true;

    if (!loading || options.length > 0) {
      return undefined;
    }
    fetchItens(parameter, api, getData, active && open, setOptions, name);

    return () => {
      active = false;
    };
    // A função é recriada a cada render; incluí-la reexecutaria a busca em loop.
    // O efeito depende apenas do gatilho listado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (!open) {
      if (selected) {
        setOptions([selected]);
      } else {
        setOptions([]);
      }
    }
    // Efeito reativo apenas ao gatilho listado; incluir as demais dependências alteraria
    // quando o efeito dispara.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderInput = (params) => {
    if (readOnly) {
      return <TextLabel {...params} id={id} label={label} />;
    }
    return (
      <TextField
        {...params}
        label={`${label}${required ? '*' : ''}`}
        fullWidth
        variant="outlined"
        InputLabelProps={{ shrink: true }}
        onBlur={handleBlur}
        error={error && touched}
        helperText={helperText ? helperText : error && touched ? error : undefined}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? <CircularProgress color="inherit" size={20} /> : null}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
      />
    );
  };

  return (
    <SelectAutoComplete
      id={id}
      renderInput={renderInput}
      options={options}
      api={api}
      name={name}
      handleChange={handleChange}
      getData={getData}
      parameter={parameter}
      open={open}
      setOpen={setOpen}
      loading={loading}
      selected={selected}
      setOptions={setOptions}
      filterName={filterNameDefault}
      getRenderOption={getRenderOption}
      disabled={disabled}
    />
  );
}

SelectAsyncPure.propTypes = {
  api: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  touched: PropTypes.bool,
  value: PropTypes.string,
  required: PropTypes.bool,
  getData: PropTypes.func,
  disabled: PropTypes.bool,
};

SelectAsyncPure.defaultProps = {
  required: false,
  touched: false,
  error: undefined,
  value: undefined,
  getData: undefined,
  disabled: false,
};
// Somente renderiza o componente em tempo real se há mudança nos props error, touched, value e disabled
function areEqual(prevProps, nextProps) {
  const { error: prevError, touched: prevTouched, value: prevValue, disabled: prevDisabled } = prevProps;
  const { error, touched, value, disabled } = nextProps;

  return error === prevError && touched === prevTouched && value === prevValue && disabled === prevDisabled;
}

const SelectAsync = React.memo(SelectAsyncPure, areEqual);

export default SelectAsync;
